import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { deepmerge } from 'deepmerge-ts';

import WorkspaceSwitcherMenuItemScss from './workspaceSwitcherMenuItem.scss?inline';

import arrowLeftIcon from '@kyndryl-design-system/shidoka-icons/svg/monochrome/16/arrow-left.svg';
import chevronRightIcon from '@kyndryl-design-system/shidoka-icons/svg/monochrome/16/chevron-right.svg';
import launchIcon from '@kyndryl-design-system/shidoka-icons/svg/monochrome/16/launch.svg';

import '../../reusable/iconSelector';

const _defaultTextStrings = {
  launchAssistiveText: 'Opens in a new tab',
};

/**
 * Workspace Switcher Menu Item component.
 * Used for both workspace items (left panel) and account items (right panel).
 * Item rows support these trailing-action combinations:
 * - favorite only
 * - launch indicator only
 * - launch indicator plus favorite
 * The launch indicator is part of the primary row button while the favorite
 * control stays pinned to the far right as a separate action.
 * @fires on-click - Emits when the item is clicked. `detail: { value: string }`
 * @fires on-favorite-change - Emits when favorite status changes. `detail: { value: string, favorited: boolean }`
 */
@customElement('kyn-workspace-switcher-menu-item')
export class WorkspaceSwitcherMenuItem extends LitElement {
  static override styles = unsafeCSS(WorkspaceSwitcherMenuItemScss);

  /** The variant of the menu item. */
  @property({ type: String })
  accessor variant: 'workspace' | 'item' | 'back' = 'item';

  /** The unique value/id of the item. */
  @property({ type: String })
  accessor value = '';

  /** The display name of the item. */
  @property({ type: String })
  accessor name = '';

  /**
   * Optional full name for the native tooltip when `name` is abbreviated in the DOM.
   * Defaults to `name`.
   */
  @property({ type: String, attribute: 'name-title' })
  accessor nameTitle = '';

  /** The count to display (workspace variant only). */
  @property({ type: Number })
  accessor count: number | null = null;

  /** Whether the item is selected. */
  @property({ type: Boolean })
  accessor selected = false;

  /** Whether the item is favorited (item variant only). */
  @property({ type: Boolean })
  accessor favorited = false;

  /** Whether to show the launch/new-tab indicator (item variant only). */
  @property({ type: Boolean })
  accessor showLaunchIndicator = false;

  /** Text string customization. */
  @property({ type: Object })
  accessor textStrings = _defaultTextStrings;

  /** Merged text strings.
   * @internal
   */
  @state()
  accessor _textStrings = _defaultTextStrings;

  /** Whether to show the favorite icon selector (item variant only). When present, it stays right-aligned. */
  @property({ type: Boolean })
  accessor showFavorite = false;

  override render() {
    const isWorkspace = this.variant === 'workspace';
    const isBack = this.variant === 'back';

    const classes = {
      'menu-item': true,
      'menu-item--workspace': isWorkspace,
      'menu-item--back': isBack,
      'menu-item--item': !isWorkspace && !isBack,
      'menu-item--selected': this.selected,
    };

    const tooltipName = this.nameTitle || this.name;

    return html`
      <div
        class=${classMap(classes)}
        role=${isBack ? 'none' : 'listitem'}
        aria-current=${this.selected ? 'true' : 'false'}
      >
        <button
          class="menu-item__select"
          title=${tooltipName}
          @click=${this._handleClick}
        >
          ${isBack
            ? html`<span class="menu-item__back-icon"
                >${unsafeSVG(arrowLeftIcon)}</span
              >`
            : null}
          <span class="menu-item__name">${this.name}</span>
          ${isWorkspace ? this._renderWorkspaceContent() : null}
          ${!isWorkspace && !isBack && this.showLaunchIndicator
            ? this._renderLaunchIndicator()
            : null}
          ${!isWorkspace && !isBack && this.showLaunchIndicator
            ? this._renderLaunchAssistiveText()
            : null}
        </button>
        ${!isWorkspace && !isBack ? this._renderItemContent() : null}
      </div>
    `;
  }

  private _renderWorkspaceContent() {
    if (this.count == null) return null;

    return html`
      <span class="menu-item__count">${this.count}</span>
      <span class="menu-item__chevron">${unsafeSVG(chevronRightIcon)}</span>
    `;
  }

  private _renderLaunchIndicator() {
    return html`
      <span class="menu-item__launch-indicator" aria-hidden="true">
        ${unsafeSVG(launchIcon)}
      </span>
    `;
  }

  private _renderLaunchAssistiveText() {
    if (!this._textStrings.launchAssistiveText) return null;

    return html`<span class="sr-only"
      >${this._textStrings.launchAssistiveText}</span
    >`;
  }

  override willUpdate(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('textStrings')) {
      this._textStrings = deepmerge(_defaultTextStrings, this.textStrings);
    }
  }

  private _renderItemContent() {
    if (!this.showFavorite) return null;

    return html`
      <div class="menu-item__actions">
        <kyn-icon-selector
          class="menu-item__favorite"
          ?checked=${this.favorited}
          value=${this.value}
          animateSelection
          onlyVisibleOnHover
          persistWhenChecked
          @on-change=${this._handleFavoriteChange}
        ></kyn-icon-selector>
      </div>
    `;
  }

  private _handleClick() {
    this.dispatchEvent(
      new CustomEvent('on-click', {
        detail: { value: this.value },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _handleFavoriteChange(e: CustomEvent) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent('on-favorite-change', {
        detail: { value: this.value, favorited: e.detail.checked },
        bubbles: true,
        composed: true,
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kyn-workspace-switcher-menu-item': WorkspaceSwitcherMenuItem;
  }
}
